import type { ComponentType, ReactNode } from 'react';
import { BellSimple, X } from '@phosphor-icons/react';
import type { IconProps } from '@phosphor-icons/react';
import { cx } from '../cx';
import { Icon } from '../Icon';
import { IconButton } from '../IconButton/IconButton';
import { Switch } from '../Switch/Switch';
import { VisuallyHidden } from '../VisuallyHidden';

export type AlertBannerProps = {
  /** What turning it on does, e.g. "Get new Helsinki jobs by email". */
  title: ReactNode;
  /** One line under the title while the alert is off. */
  body?: ReactNode;
  /** The line once the alert is on ("Alert on: new jobs arrive by email each morning"). */
  enabledBody?: ReactNode;
  /** Where alerts are managed; linked once the alert is on. */
  manage?: { href: string; label: ReactNode };
  enabled: boolean;
  onEnabledChange: (enabled: boolean) => void;
  /** Accessible name of the switch, localised by the caller. */
  toggleLabel: string;
  /** The bell: this is an alert. Never binoculars. */
  icon?: ComponentType<IconProps>;
  /**
   * One line, about 48px: icon, text, switch and close. The title and body run together (two lines
   * on a phone). For a page that must stay short, such as the search results.
   */
  compact?: boolean;
  className?: string;
} & (
  | { onDismiss?: undefined; dismissLabel?: undefined }
  | {
      /** Dismissable; the caller keeps it dismissed for the rest of the session. */
      onDismiss: () => void;
      /** Accessible name of the dismiss button, required with onDismiss. */
      dismissLabel: string;
    }
);

/** A neutral panel that turns the current context (usually a search) into an email alert with one switch. */
export function AlertBanner({
  title,
  body,
  enabledBody,
  manage,
  enabled,
  onEnabledChange,
  toggleLabel,
  onDismiss,
  dismissLabel,
  icon = BellSimple,
  compact = false,
  className,
}: AlertBannerProps) {
  const detail = enabled ? (enabledBody ?? body) : body;
  const manageLink =
    enabled && manage ? (
      <a
        href={manage.href}
        className="rounded-jc-info font-semibold text-jc-ink underline underline-offset-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-jc-focus"
      >
        {manage.label}
      </a>
    ) : null;
  if (compact) {
    return (
      <section
        className={cx(
          'flex min-h-12 items-center gap-3 rounded-jc-container bg-jc-elevated py-1.5 pl-3 pr-2 text-jc-ink',
          className,
        )}
      >
        <Icon icon={icon} size="md" className="shrink-0" />
        <p className="m-0 min-w-0 flex-1 text-sm max-sm:line-clamp-2 sm:truncate">
          <span className="font-semibold">{title}</span>
          {detail ? <span className="text-jc-ink-secondary"> {detail}</span> : null}
          {manageLink ? <> {manageLink}</> : null}
        </p>
        <Switch
          label={<VisuallyHidden>{toggleLabel}</VisuallyHidden>}
          labelPosition="end"
          checked={enabled}
          onCheckedChange={onEnabledChange}
        />
        {onDismiss ? <IconButton icon={X} label={dismissLabel} variant="ghost" size="sm" onClick={onDismiss} /> : null}
      </section>
    );
  }
  return (
    <section className={cx('flex items-center gap-4 rounded-jc-container bg-jc-elevated p-4 text-jc-ink', className)}>
      <span className="grid size-10 shrink-0 place-items-center rounded-full bg-jc-surface text-jc-ink">
        <Icon icon={icon} size="md" />
      </span>
      <div className="flex min-w-0 flex-1 flex-col gap-0.5">
        <p className="text-sm font-semibold">{title}</p>
        {(enabled ? (enabledBody ?? body) : body) || (enabled && manage) ? (
          <div className="text-sm text-jc-ink-secondary">
            {enabled ? (enabledBody ?? body) : body}
            {enabled && manage ? (
              <>
                {' '}
                <a
                  href={manage.href}
                  className="rounded-jc-info font-semibold text-jc-ink underline underline-offset-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-jc-focus"
                >
                  {manage.label}
                </a>
              </>
            ) : null}
          </div>
        ) : null}
      </div>
      <Switch
        label={<VisuallyHidden>{toggleLabel}</VisuallyHidden>}
        labelPosition="end"
        checked={enabled}
        onCheckedChange={onEnabledChange}
      />
      {onDismiss ? <IconButton icon={X} label={dismissLabel} variant="ghost" size="sm" onClick={onDismiss} /> : null}
    </section>
  );
}
